<script setup lang="ts">
import { inject, onScopeDispose, ref, watch, type Ref } from "vue";
import GeneratedPersonSearchTable from "./GeneratedPersonSearchTable.vue";
import { useDamageWaiverService } from "../../../cbi-generator/frontend/composables/useDamageWaiverFrontendService";
import type { PersonStaff } from "../../../cbi-generator/generated/dto/PersonStaff";
import type { DamageWaiverStaffSearchParams } from "../../../cbi-generator/generated/dto/DamageWaiverStaffSearchParams";
import type { DamageWaiverStaffSearchResult } from "../../../cbi-generator/generated/dto/DamageWaiverStaffSearchResult";
import { DamageWaiverStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/DamageWaiverStaffSearchParamsMeta";
import { DamageWaiverStaffSearchResultMeta } from "../../../cbi-generator/generated/dto/DamageWaiverStaffSearchResultMeta";
import type { DamageWaiverMemberSearchParams } from "../../../cbi-generator/generated/dto/DamageWaiverMemberSearchParams";
import type { DamageWaiverMemberSearchResult } from "../../../cbi-generator/generated/dto/DamageWaiverMemberSearchResult";
import { DamageWaiverMemberSearchParamsMeta } from "../../../cbi-generator/generated/dto/DamageWaiverMemberSearchParamsMeta";
import { DamageWaiverMemberSearchResultMeta } from "../../../cbi-generator/generated/dto/DamageWaiverMemberSearchResultMeta";
import { PurchasableState } from "../../../cbi-generator/generated/dto/PurchasableState";
import type { GeneratedTableConfig } from "@/composables/generated/contracts";
import { errorMessage } from "@/composables/generated/contracts";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useUtility } from "@/composables/useUtility";

const props = withDefaults(
  defineProps<{ personId: number; mode?: "staff" | "member" }>(),
  { mode: "staff" },
);
type Row = DamageWaiverStaffSearchResult | DamageWaiverMemberSearchResult;
const isMember = props.mode === "member";
const emit = defineEmits<{ active: [active: boolean | null] }>();
const person = inject<Ref<PersonStaff | null>>("person");
const service = useDamageWaiverService();
const { formatDateNY, formatCost } = useUtility();
const statusError = ref("");
let version = 0;
onScopeDispose(() => ++version);
async function loadStatus() {
  const request = ++version;
  emit("active", null);
  statusError.value = "";
  // The active-waiver status reads a staff-only relation.
  if (isMember || !person?.value) return;
  try {
    const waivers = await person.value.damageWaiversLazy();
    if (request !== version) return;
    const now = Date.now();
    emit(
      "active",
      waivers.some(
        (row) =>
          row.voidCloseId == null &&
          row.purchasableState === PurchasableState.ACTIVE &&
          row.startDate != null &&
          row.startDate.getTime() <= now &&
          (row.expirationDate == null || row.expirationDate.getTime() > now),
      ),
    );
  } catch (cause) {
    if (request === version) statusError.value = errorMessage(cause);
  }
}
watch(() => person?.value, loadStatus, { immediate: true });
const staffConfig: GeneratedTableConfig<
  DamageWaiverStaffSearchResult,
  DamageWaiverStaffSearchParams
> = {
  id: `person-${props.personId}-damage-waivers`,
  rowKey: "waiverId",
  resultMeta: DamageWaiverStaffSearchResultMeta,
  searchMeta: DamageWaiverStaffSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<DamageWaiverStaffSearchParams>(
      DamageWaiverStaffSearchParamsMeta,
    ),
    personIdExact: props.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchDamageWaiverStaff(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: [
    "waiverId",
    "price",
    "voided",
    "purchasableState",
    "purchaseDate",
    "startDate",
    "expirationDate",
  ],
  defaultSort: [{ key: "purchaseDate", order: "desc" }],
};
// Member search is scoped to the caller's household server-side.
const memberConfig: GeneratedTableConfig<
  DamageWaiverMemberSearchResult,
  DamageWaiverMemberSearchParams
> = {
  ...staffConfig,
  id: `person-${props.personId}-damage-waivers-member`,
  rowKey: "waiverId",
  resultMeta: DamageWaiverMemberSearchResultMeta,
  searchMeta: DamageWaiverMemberSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<DamageWaiverMemberSearchParams>(
      DamageWaiverMemberSearchParamsMeta,
    ),
    personIdExact: props.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchDamageWaiverMember(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
};
const config = (isMember ? memberConfig : staffConfig) as GeneratedTableConfig<
  Row,
  DamageWaiverStaffSearchParams
>;
const headers = [
  {
    key: "price",
    title: "Price",
    computeValue: (row: Row) =>
      row.price == null ? "--" : formatCost(row.price),
  },
  {
    key: "voided",
    title: "Voided",
    sortable: false,
    computeValue: (row: Row) => (row.voidCloseId != null ? "Yes" : "No"),
  },
  {
    key: "purchaseDate",
    title: "Purchase Date",
    computeValue: (row: Row) => formatDateNY(row.purchaseDate),
  },
  {
    key: "startDate",
    title: "Start Date",
    computeValue: (row: Row) => formatDateNY(row.startDate),
  },
  {
    key: "expirationDate",
    title: "Expiration Date",
    computeValue: (row: Row) => formatDateNY(row.expirationDate),
  },
];
</script>

<template>
  <v-alert v-if="statusError" type="error" role="alert" class="mb-4">
    {{ statusError }} <v-btn @click="loadStatus">Retry waiver status</v-btn>
  </v-alert>
  <GeneratedPersonSearchTable
    :config="config"
    :hide-export="isMember"
    :headers="headers"
    :filter-keys="[
      'purchasableStateExact',
      'startDateGreaterEq',
      'expirationDateLessEq',
    ]"
    refresh-flag="damageWaiverDataState"
  />
</template>
